/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { AgreementParseRecordRead } from '../models/AgreementParseRecordRead';
import type { BlockCalculationsPayload } from '../models/BlockCalculationsPayload';
import type { BlockInterpolationPayload } from '../models/BlockInterpolationPayload';
import type { BlockRead } from '../models/BlockRead';
import type { BulkDeleteTasksPayload } from '../models/BulkDeleteTasksPayload';
import type { BulkMarkCompletePayload } from '../models/BulkMarkCompletePayload';
import type { BulkReassignTasksPayload } from '../models/BulkReassignTasksPayload';
import type { BulkUpdateDueDatePayload } from '../models/BulkUpdateDueDatePayload';
import type { CollectionPlanUpdate } from '../models/CollectionPlanUpdate';
import type { CollectionPlanWithDetails } from '../models/CollectionPlanWithDetails';
import type { CompletedReasonEnum } from '../models/CompletedReasonEnum';
import type { CreateDeliverableFromTemplatePayload } from '../models/CreateDeliverableFromTemplatePayload';
import type { DataExtractionTaskOverview } from '../models/DataExtractionTaskOverview';
import type { DataExtractionTaskOverviewPayload } from '../models/DataExtractionTaskOverviewPayload';
import type { DataServicesTaskEnrichment } from '../models/DataServicesTaskEnrichment';
import type { DataServicesTaskEnrichmentsPayload } from '../models/DataServicesTaskEnrichmentsPayload';
import type { DataServicesTaskPage } from '../models/DataServicesTaskPage';
import type { DeliverableParseResult } from '../models/DeliverableParseResult';
import type { DeliverableWithEntitiesWithStatuses } from '../models/DeliverableWithEntitiesWithStatuses';
import type { DeliverableWithStatuses } from '../models/DeliverableWithStatuses';
import type { EntityComplianceStatuses } from '../models/EntityComplianceStatuses';
import type { EntityFiltersPayload } from '../models/EntityFiltersPayload';
import type { EntityWithTemplateMeta } from '../models/EntityWithTemplateMeta';
import type { FormulaValidationResult } from '../models/FormulaValidationResult';
import type { GeneratePdfPayload } from '../models/GeneratePdfPayload';
import type { ListClusterTasksFilters } from '../models/ListClusterTasksFilters';
import type { ListComplianceStatusPayload } from '../models/ListComplianceStatusPayload';
import type { ListDeliverablesWithEntitiesPayload } from '../models/ListDeliverablesWithEntitiesPayload';
import type { ListTaskResponseForBlockTypePayload } from '../models/ListTaskResponseForBlockTypePayload';
import type { MonthEnum } from '../models/MonthEnum';
import type { OperationReviewEntry } from '../models/OperationReviewEntry';
import type { PaginatedClusterTaskSummary } from '../models/PaginatedClusterTaskSummary';
import type { PaginatedResult } from '../models/PaginatedResult';
import type { ParseDeliverablesPayload } from '../models/ParseDeliverablesPayload';
import type { ParseOnboardingChainPayload } from '../models/ParseOnboardingChainPayload';
import type { PauseCollectionPlanPayload } from '../models/PauseCollectionPlanPayload';
import type { PreviewCollectionDatePayload } from '../models/PreviewCollectionDatePayload';
import type { PreviewTaskTemplatePayload } from '../models/PreviewTaskTemplatePayload';
import type { ReassignTaskPayload } from '../models/ReassignTaskPayload';
import type { ReviewState } from '../models/ReviewState';
import type { SaveTaskResponsesPayload } from '../models/SaveTaskResponsesPayload';
import type { SourceBboxResponse } from '../models/SourceBboxResponse';
import type { StartDateWithDetails } from '../models/StartDateWithDetails';
import type { TaskRead } from '../models/TaskRead';
import type { TaskResponseRead } from '../models/TaskResponseRead';
import type { TaskSummary } from '../models/TaskSummary';
import type { TaskTemplateGroupCreate } from '../models/TaskTemplateGroupCreate';
import type { TaskTemplateGroupPayload } from '../models/TaskTemplateGroupPayload';
import type { TaskTemplateGroupRead } from '../models/TaskTemplateGroupRead';
import type { TaskTemplateRead } from '../models/TaskTemplateRead';
import type { TaskTemplateWithBlocks } from '../models/TaskTemplateWithBlocks';
import type { TaskWithBlocks } from '../models/TaskWithBlocks';
import type { UnresolvedParseRecord } from '../models/UnresolvedParseRecord';
import type { UpdateDeliverableNormalizedNamePayload } from '../models/UpdateDeliverableNormalizedNamePayload';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class CollectionPlansService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * List Namespace Task Templates
     * @param namespaceId
     * @returns TaskTemplateRead Successful Response
     * @throws ApiError
     */
    public listNamespaceTaskTemplates(
        namespaceId: string,
    ): CancelablePromise<Array<TaskTemplateRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/collection-plans/list-namespace-task-templates',
            query: {
                'namespace_id': namespaceId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Namespace Task Templates With Usage
     * @param namespaceId
     * @returns TaskTemplateWithBlocks Successful Response
     * @throws ApiError
     */
    public listNamespaceTaskTemplatesWithUsage(
        namespaceId: string,
    ): CancelablePromise<Array<TaskTemplateWithBlocks>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/collection-plans/list-namespace-task-templates-with-usage',
            query: {
                'namespace_id': namespaceId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Unique Task Names For Namespace
     * Get all unique task names for tasks in a namespace.
     * Used for populating the task name filter dropdown.
     * @param namespaceId
     * @returns string Successful Response
     * @throws ApiError
     */
    public listUniqueTaskNamesForNamespace(
        namespaceId: string,
    ): CancelablePromise<Array<string>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/collection-plans/list-unique-task-names-for-namespace',
            query: {
                'namespace_id': namespaceId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Cluster Tasks For Namespace
     * @param namespaceId
     * @param sortBy
     * @param sortOrder
     * @param page
     * @param pageSize
     * @param requestBody
     * @returns PaginatedClusterTaskSummary Successful Response
     * @throws ApiError
     */
    public listClusterTasksForNamespace(
        namespaceId: string,
        sortBy: string = 'effective_date',
        sortOrder: string = 'desc',
        page: number = 1,
        pageSize: number = 50,
        requestBody?: (ListClusterTasksFilters | null),
    ): CancelablePromise<PaginatedClusterTaskSummary> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/collection-plans/list-cluster-tasks-for-namespace',
            query: {
                'namespace_id': namespaceId,
                'sort_by': sortBy,
                'sort_order': sortOrder,
                'page': page,
                'page_size': pageSize,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Delete Cluster Task Template
     * @param taskTemplateId
     * @returns any Successful Response
     * @throws ApiError
     */
    public deleteClusterTaskTemplate(
        taskTemplateId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/collection-plans/delete-cluster-task-template',
            query: {
                'task_template_id': taskTemplateId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Task Template References
     * @param taskTemplateId
     * @returns any Successful Response
     * @throws ApiError
     */
    public getTaskTemplateReferences(
        taskTemplateId: string,
    ): CancelablePromise<Record<string, Array<Record<string, any>>>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/collection-plans/get-task-template-references/{task_template_id}',
            path: {
                'task_template_id': taskTemplateId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Task Template
     * @param taskTemplateId
     * @returns TaskTemplateWithBlocks Successful Response
     * @throws ApiError
     */
    public getTaskTemplate(
        taskTemplateId: string,
    ): CancelablePromise<TaskTemplateWithBlocks> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/collection-plans/get-task-template/{task_template_id}',
            path: {
                'task_template_id': taskTemplateId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Validate Block Formulas
     * @param requestBody
     * @returns FormulaValidationResult Successful Response
     * @throws ApiError
     */
    public validateBlockFormulas(
        requestBody: Array<BlockRead>,
    ): CancelablePromise<Array<FormulaValidationResult>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/collection-plans/validate-block-formulas',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Create Namespace Task Template
     * @param clusterId
     * @param namespaceId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public createNamespaceTaskTemplate(
        clusterId: string,
        namespaceId: string,
        requestBody: TaskTemplateWithBlocks,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/collection-plans/create-namespace-task-template',
            query: {
                'cluster_id': clusterId,
                'namespace_id': namespaceId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Update Namespace Task Template
     * @param taskTemplateId
     * @param requestBody
     * @returns any[] Successful Response
     * @throws ApiError
     */
    public updateNamespaceTaskTemplate(
        taskTemplateId: string,
        requestBody: TaskTemplateWithBlocks,
    ): CancelablePromise<any[]> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/collection-plans/update-namespace-task-template',
            query: {
                'task_template_id': taskTemplateId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Update Group For Namespace Task Template
     * @param taskTemplateId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public updateGroupForNamespaceTaskTemplate(
        taskTemplateId: string,
        requestBody: TaskTemplateGroupPayload,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/collection-plans/update-group-for-namespace-task-template',
            query: {
                'task_template_id': taskTemplateId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Update Namespace Tasks For Task Template
     * @param taskTemplateId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public updateNamespaceTasksForTaskTemplate(
        taskTemplateId: string,
        requestBody: CollectionPlanUpdate,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/collection-plans/update-namespace-tasks-for-task-template',
            query: {
                'task_template_id': taskTemplateId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Entities With Namespace Template Meta
     * @param clusterId
     * @param namespaceId
     * @param taskTemplateId
     * @param requestBody
     * @returns EntityWithTemplateMeta Successful Response
     * @throws ApiError
     */
    public getEntitiesWithNamespaceTemplateMeta(
        clusterId: string,
        namespaceId: string,
        taskTemplateId: string,
        requestBody: EntityFiltersPayload,
    ): CancelablePromise<Array<EntityWithTemplateMeta>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/collection-plans/get-entities-with-namespace-template-meta',
            query: {
                'cluster_id': clusterId,
                'namespace_id': namespaceId,
                'task_template_id': taskTemplateId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Collection Plan
     * @param workflowId
     * @returns CollectionPlanWithDetails Successful Response
     * @throws ApiError
     */
    public getCollectionPlan(
        workflowId: string,
    ): CancelablePromise<CollectionPlanWithDetails> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/collection-plans/get-collection-plan/{workflow_id}',
            path: {
                'workflow_id': workflowId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Retry Failed Submit
     * @param taskId
     * @param pdfId
     * @returns TaskRead Successful Response
     * @throws ApiError
     */
    public retryFailedSubmit(
        taskId: string,
        pdfId?: (string | null),
    ): CancelablePromise<TaskRead> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/collection-plans/retry-failed-submit/{task_id}',
            path: {
                'task_id': taskId,
            },
            query: {
                'pdf_id': pdfId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Generate Pdf For Task And Update Template
     * @param taskId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public generatePdfForTaskAndUpdateTemplate(
        taskId: string,
        requestBody: GeneratePdfPayload,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/collection-plans/generate-pdf-for-task-and-update-template/{task_id}',
            path: {
                'task_id': taskId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Delete Collection Plan
     * @param workflowId
     * @returns any Successful Response
     * @throws ApiError
     */
    public deleteCollectionPlan(
        workflowId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/collection-plans/delete-collection-plan/{workflow_id}',
            path: {
                'workflow_id': workflowId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Pause Collection Plan
     * @param workflowId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public pauseCollectionPlan(
        workflowId: string,
        requestBody: PauseCollectionPlanPayload,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/collection-plans/pause-collection-plan/{workflow_id}',
            path: {
                'workflow_id': workflowId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Task With Blocks
     * @param taskId
     * @returns TaskWithBlocks Successful Response
     * @throws ApiError
     */
    public getTaskWithBlocks(
        taskId: string,
    ): CancelablePromise<TaskWithBlocks> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/collection-plans/get-task-with-blocks/{task_id}',
            path: {
                'task_id': taskId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Tasks For Membership
     * @param membershipId
     * @returns TaskSummary Successful Response
     * @throws ApiError
     */
    public listTasksForMembership(
        membershipId: string,
    ): CancelablePromise<Array<TaskSummary>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/collection-plans/list-tasks-for-membership/{membership_id}',
            path: {
                'membership_id': membershipId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Preview Task With Blocks
     * @param taskTemplateId
     * @returns TaskWithBlocks Successful Response
     * @throws ApiError
     */
    public previewTaskWithBlocks(
        taskTemplateId: string,
    ): CancelablePromise<TaskWithBlocks> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/collection-plans/preview-task-with-blocks/{task_template_id}',
            path: {
                'task_template_id': taskTemplateId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Update Task Due Date
     * Called when a Cluster marks a task as complete without submitting data
     * @param taskId
     * @param dueDate
     * @returns any Successful Response
     * @throws ApiError
     */
    public updateTaskDueDate(
        taskId: string,
        dueDate: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/collection-plans/update-task-due-date/{task_id}',
            path: {
                'task_id': taskId,
            },
            query: {
                'due_date': dueDate,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Save Task Responses
     * Called when a Borrower saves a task as a draft
     * @param taskId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public saveTaskResponses(
        taskId: string,
        requestBody: SaveTaskResponsesPayload,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/collection-plans/save-task-responses/{task_id}',
            path: {
                'task_id': taskId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Task Responses For Task
     * @param taskId
     * @returns TaskResponseRead Successful Response
     * @throws ApiError
     */
    public listTaskResponsesForTask(
        taskId: string,
    ): CancelablePromise<Array<TaskResponseRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/collection-plans/list-task-responses-for-task/{task_id}',
            path: {
                'task_id': taskId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Generate Enriched Start Date Candidates For Schedule
     * @param fiscalYearEndMonth
     * @param requestBody
     * @returns StartDateWithDetails Successful Response
     * @throws ApiError
     */
    public generateEnrichedStartDateCandidatesForSchedule(
        fiscalYearEndMonth: MonthEnum,
        requestBody: PreviewCollectionDatePayload,
    ): CancelablePromise<Array<StartDateWithDetails>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/collection-plans/generate-enriched-start-date-candidates-for-rrule',
            query: {
                'fiscal_year_end_month': fiscalYearEndMonth,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Data Services Tasks Paginated
     * @param includesCompletedTasks
     * @param search
     * @param page
     * @param pageSize
     * @param sortBy
     * @param sortOrder
     * @returns DataServicesTaskPage Successful Response
     * @throws ApiError
     */
    public listDataServicesTasksPaginated(
        includesCompletedTasks: boolean = false,
        search?: (string | null),
        page: number = 1,
        pageSize: number = 50,
        sortBy?: (string | null),
        sortOrder: 'asc' | 'desc' = 'desc',
    ): CancelablePromise<DataServicesTaskPage> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/collection-plans/list-data-services-tasks-paginated',
            query: {
                'includes_completed_tasks': includesCompletedTasks,
                'search': search,
                'page': page,
                'page_size': pageSize,
                'sort_by': sortBy,
                'sort_order': sortOrder,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Data Services Task Enrichments
     * @param requestBody
     * @returns DataServicesTaskEnrichment Successful Response
     * @throws ApiError
     */
    public listDataServicesTaskEnrichments(
        requestBody: DataServicesTaskEnrichmentsPayload,
    ): CancelablePromise<Array<DataServicesTaskEnrichment>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/collection-plans/list-data-services-task-enrichments',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Data Extraction Task Overview
     * @param requestBody
     * @returns DataExtractionTaskOverview Successful Response
     * @throws ApiError
     */
    public listDataExtractionTaskOverview(
        requestBody?: (DataExtractionTaskOverviewPayload | null),
    ): CancelablePromise<Array<DataExtractionTaskOverview>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/collection-plans/list-data-extraction-task-overview',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Data Extraction Task
     * @param dataExtractionId
     * @returns any Successful Response
     * @throws ApiError
     */
    public getDataExtractionTask(
        dataExtractionId: any,
    ): CancelablePromise<(TaskRead | null)> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/collection-plans/get-data-extraction-task',
            query: {
                'data_extraction_id': dataExtractionId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Outstanding Tasks For Template
     * @param taskTemplateId
     * @returns TaskSummary Successful Response
     * @throws ApiError
     */
    public listOutstandingTasksForTemplate(
        taskTemplateId: string,
    ): CancelablePromise<Array<TaskSummary>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/collection-plans/list-outstanding-tasks-for-template/{task_template_id}',
            path: {
                'task_template_id': taskTemplateId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Prefillable Tasks For Template
     * @param taskTemplateId
     * @param entityId
     * @returns TaskSummary Successful Response
     * @throws ApiError
     */
    public listPrefillableTasksForTemplate(
        taskTemplateId: string,
        entityId: string,
    ): CancelablePromise<Array<TaskSummary>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/collection-plans/list-prefillable-tasks-for-template/{task_template_id}',
            path: {
                'task_template_id': taskTemplateId,
            },
            query: {
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Existing Value By Block Id For Task
     * @param taskId
     * @returns any Successful Response
     * @throws ApiError
     */
    public getExistingValueByBlockIdForTask(
        taskId: string,
    ): CancelablePromise<Record<string, (number | string | Record<string, any> | boolean | null)>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/collection-plans/get-existing-value-by-block-id-for-task/{task_id}',
            path: {
                'task_id': taskId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Create Task Template Group
     * @param namespaceId
     * @param requestBody
     * @returns TaskTemplateGroupRead Successful Response
     * @throws ApiError
     */
    public createTaskTemplateGroup(
        namespaceId: string,
        requestBody: TaskTemplateGroupCreate,
    ): CancelablePromise<TaskTemplateGroupRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/collection-plans/create-task-template-group',
            query: {
                'namespace_id': namespaceId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Task Template Groups
     * @param namespaceId
     * @returns TaskTemplateGroupRead Successful Response
     * @throws ApiError
     */
    public listTaskTemplateGroups(
        namespaceId: string,
    ): CancelablePromise<Array<TaskTemplateGroupRead>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/collection-plans/list-task-template-groups',
            query: {
                'namespace_id': namespaceId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Task Template Group
     * @param groupId
     * @param namespaceId
     * @returns TaskTemplateGroupRead Successful Response
     * @throws ApiError
     */
    public getTaskTemplateGroup(
        groupId: string,
        namespaceId: string,
    ): CancelablePromise<TaskTemplateGroupRead> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/collection-plans/get-task-template-groups/{group_id}',
            path: {
                'group_id': groupId,
            },
            query: {
                'namespace_id': namespaceId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Update Task Template Group
     * @param namespaceId
     * @param taskTemplateGroupId
     * @param requestBody
     * @returns TaskTemplateGroupRead Successful Response
     * @throws ApiError
     */
    public updateTaskTemplateGroup(
        namespaceId: string,
        taskTemplateGroupId: string,
        requestBody: TaskTemplateGroupCreate,
    ): CancelablePromise<TaskTemplateGroupRead> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/collection-plans/update-task-template-groups/{namespace_id}/{task_template_group_id}',
            path: {
                'namespace_id': namespaceId,
                'task_template_group_id': taskTemplateGroupId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Delete Task Template Group
     * @param namespaceId
     * @param groupId
     * @returns any Successful Response
     * @throws ApiError
     */
    public deleteTaskTemplateGroup(
        namespaceId: string,
        groupId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/collection-plans/delete-task-template-groups/{namespace_id}/{group_id}',
            path: {
                'namespace_id': namespaceId,
                'group_id': groupId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Find Source Bbox
     * @param documentId
     * @param searchText
     * @param page
     * @param snippet
     * @returns any Successful Response
     * @throws ApiError
     */
    public findSourceBbox(
        documentId: string,
        searchText: string,
        page?: (number | null),
        snippet?: (string | null),
    ): CancelablePromise<(SourceBboxResponse | null)> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/collection-plans/source-bbox',
            query: {
                'document_id': documentId,
                'search_text': searchText,
                'page': page,
                'snippet': snippet,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Audit Logs For Deliverables For Entity
     * @param entityId
     * @param cursor
     * @param pageSize
     * @returns PaginatedResult Successful Response
     * @throws ApiError
     */
    public listAuditLogsForDeliverablesForEntity(
        entityId: string,
        cursor?: (string | null),
        pageSize: number = 20,
    ): CancelablePromise<PaginatedResult> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/collection-plans/list-audit-logs-for-deliverables-for-entity/{entity_id}',
            path: {
                'entity_id': entityId,
            },
            query: {
                'cursor': cursor,
                'page_size': pageSize,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Audit Logs For Deliverable
     * @param taskTemplateId
     * @param cursor
     * @param pageSize
     * @returns PaginatedResult Successful Response
     * @throws ApiError
     */
    public listAuditLogsForDeliverable(
        taskTemplateId: string,
        cursor?: (string | null),
        pageSize: number = 20,
    ): CancelablePromise<PaginatedResult> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/collection-plans/list-audit-logs-for-deliverable/{task_template_id}',
            path: {
                'task_template_id': taskTemplateId,
            },
            query: {
                'cursor': cursor,
                'page_size': pageSize,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Collection Plans For Entity
     * @param entityId
     * @returns CollectionPlanWithDetails Successful Response
     * @throws ApiError
     */
    public listCollectionPlansForEntity(
        entityId: string,
    ): CancelablePromise<Array<CollectionPlanWithDetails>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/entities/{entity_id}/collection-plans/list-collection-plans',
            path: {
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Ad Hoc Collection Plans For Entity
     * @param entityId
     * @returns CollectionPlanWithDetails Successful Response
     * @throws ApiError
     */
    public listAdHocCollectionPlansForEntity(
        entityId: string,
    ): CancelablePromise<Array<CollectionPlanWithDetails>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/entities/{entity_id}/collection-plans/list-ad-hoc-collection-plans',
            path: {
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Resume Collection Plan
     * @param entityId
     * @param workflowId
     * @returns any Successful Response
     * @throws ApiError
     */
    public resumeCollectionPlan(
        entityId: string,
        workflowId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/entities/{entity_id}/collection-plans/resume-collection-plan/{workflow_id}',
            path: {
                'entity_id': entityId,
                'workflow_id': workflowId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Downloadable File Ids
     * @param entityId
     * @param requestBody
     * @returns string Successful Response
     * @throws ApiError
     */
    public listDownloadableFileIds(
        entityId: string,
        requestBody: ListTaskResponseForBlockTypePayload,
    ): CancelablePromise<Record<string, Array<string>>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/entities/{entity_id}/collection-plans/list-downloadable-file-ids',
            path: {
                'entity_id': entityId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Interpolated Body By Block Id
     * @param entityId
     * @param requestBody
     * @returns string Successful Response
     * @throws ApiError
     */
    public getInterpolatedBodyByBlockId(
        entityId: string,
        requestBody: BlockInterpolationPayload,
    ): CancelablePromise<Record<string, string>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/entities/{entity_id}/collection-plans/get-interpolated-body-by-block',
            path: {
                'entity_id': entityId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Calculate Computed Fields
     * @param entityId
     * @param effectiveDate
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public calculateComputedFields(
        entityId: string,
        effectiveDate: string,
        requestBody: BlockCalculationsPayload,
    ): CancelablePromise<Record<string, (number | string | boolean | null)>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/entities/{entity_id}/collection-plans/calculate-computed-fields',
            path: {
                'entity_id': entityId,
            },
            query: {
                'effective_date': effectiveDate,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Deliverables With Status Over Time For Entity
     * @param entityId
     * @returns DeliverableWithStatuses Successful Response
     * @throws ApiError
     */
    public listDeliverablesWithStatusOverTimeForEntity(
        entityId: string,
    ): CancelablePromise<Array<DeliverableWithStatuses>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/entities/{entity_id}/collection-plans/list-deliverables-with-status-over-time-for-entity',
            path: {
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Generate Start Date Candidates For Entity
     * @param entityId
     * @param fiscalYearEndMonth
     * @returns StartDateWithDetails Successful Response
     * @throws ApiError
     */
    public generateStartDateCandidatesForEntity(
        entityId: string,
        fiscalYearEndMonth: MonthEnum,
    ): CancelablePromise<Record<string, Array<StartDateWithDetails>>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/entities/{entity_id}/collection-plans/generate-start-date-candidates-for-entity/{fiscal_year_end_month}',
            path: {
                'entity_id': entityId,
                'fiscal_year_end_month': fiscalYearEndMonth,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Existing Value By Block Id
     * @param entityId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public getExistingValueByBlockId(
        entityId: string,
        requestBody: PreviewTaskTemplatePayload,
    ): CancelablePromise<Record<string, (number | string | Record<string, any> | boolean | null)>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/entities/{entity_id}/collection-plans/get-existing-value-by-block-id',
            path: {
                'entity_id': entityId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Create Deliverable From Template
     * @param entityId
     * @param clusterId
     * @param requestBody
     * @returns CollectionPlanWithDetails Successful Response
     * @throws ApiError
     */
    public createDeliverableFromTemplate(
        entityId: string,
        clusterId: string,
        requestBody: CreateDeliverableFromTemplatePayload,
    ): CancelablePromise<CollectionPlanWithDetails> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/entities/{entity_id}/collection-plans/create-deliverable-from-template/',
            path: {
                'entity_id': entityId,
            },
            query: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Parse Deliverables From Document
     * @param entityId
     * @param clusterId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public parseDeliverablesFromDocument(
        entityId: string,
        clusterId: string,
        requestBody: ParseDeliverablesPayload,
    ): CancelablePromise<Record<string, any>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/entities/{entity_id}/collection-plans/parse-deliverables-from-document/',
            path: {
                'entity_id': entityId,
            },
            query: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Parse Onboarding Chain
     * @param entityId
     * @param clusterId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public parseOnboardingChain(
        entityId: string,
        clusterId: string,
        requestBody: ParseOnboardingChainPayload,
    ): CancelablePromise<Record<string, any>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/entities/{entity_id}/collection-plans/parse-onboarding-chain/',
            path: {
                'entity_id': entityId,
            },
            query: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Unresolved Parse Records For Entity
     * @param entityId
     * @returns UnresolvedParseRecord Successful Response
     * @throws ApiError
     */
    public listUnresolvedParseRecordsForEntity(
        entityId: string,
    ): CancelablePromise<Array<UnresolvedParseRecord>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/entities/{entity_id}/collection-plans/parse-record/unresolved',
            path: {
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Dismiss Parse Record
     * @param entityId
     * @param parseRecordId
     * @returns AgreementParseRecordRead Successful Response
     * @throws ApiError
     */
    public dismissParseRecord(
        entityId: string,
        parseRecordId: string,
    ): CancelablePromise<AgreementParseRecordRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/entities/{entity_id}/collection-plans/parse-record/{parse_record_id}/dismiss',
            path: {
                'entity_id': entityId,
                'parse_record_id': parseRecordId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Parse Result For Parse Record
     * @param entityId
     * @param parseRecordId
     * @returns any Successful Response
     * @throws ApiError
     */
    public getParseResultForParseRecord(
        entityId: string,
        parseRecordId: string,
    ): CancelablePromise<(DeliverableParseResult | null)> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/entities/{entity_id}/collection-plans/parse-record/{parse_record_id}/parse-result',
            path: {
                'entity_id': entityId,
                'parse_record_id': parseRecordId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Review State For Parse Record
     * @param entityId
     * @param parseRecordId
     * @returns ReviewState Successful Response
     * @throws ApiError
     */
    public getReviewStateForParseRecord(
        entityId: string,
        parseRecordId: string,
    ): CancelablePromise<ReviewState> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/entities/{entity_id}/collection-plans/parse-record/{parse_record_id}/review-state',
            path: {
                'entity_id': entityId,
                'parse_record_id': parseRecordId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Set Operation Review Status
     * @param entityId
     * @param parseRecordId
     * @param opKey
     * @param requestBody
     * @returns ReviewState Successful Response
     * @throws ApiError
     */
    public setOperationReviewStatus(
        entityId: string,
        parseRecordId: string,
        opKey: string,
        requestBody: OperationReviewEntry,
    ): CancelablePromise<ReviewState> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/entities/{entity_id}/collection-plans/parse-record/{parse_record_id}/operations/{op_key}',
            path: {
                'entity_id': entityId,
                'parse_record_id': parseRecordId,
                'op_key': opKey,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Clear Operation Review Status
     * @param entityId
     * @param parseRecordId
     * @param opKey
     * @returns ReviewState Successful Response
     * @throws ApiError
     */
    public clearOperationReviewStatus(
        entityId: string,
        parseRecordId: string,
        opKey: string,
    ): CancelablePromise<ReviewState> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/entities/{entity_id}/collection-plans/parse-record/{parse_record_id}/operations/{op_key}',
            path: {
                'entity_id': entityId,
                'parse_record_id': parseRecordId,
                'op_key': opKey,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Unique Task Names For Cluster
     * Get all unique task names for cluster tasks across all namespaces.
     * @param clusterId
     * @returns string Successful Response
     * @throws ApiError
     */
    public listUniqueTaskNamesForCluster(
        clusterId: string,
    ): CancelablePromise<Array<string>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/clusters/{cluster_id}/collection-plans/list-unique-task-names-for-cluster',
            path: {
                'cluster_id': clusterId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Cluster Tasks
     * @param clusterId
     * @param sortBy
     * @param sortOrder
     * @param page
     * @param pageSize
     * @param requestBody
     * @returns PaginatedClusterTaskSummary Successful Response
     * @throws ApiError
     */
    public listClusterTasks(
        clusterId: string,
        sortBy: string = 'effective_date',
        sortOrder: string = 'desc',
        page: number = 1,
        pageSize: number = 50,
        requestBody?: (ListClusterTasksFilters | null),
    ): CancelablePromise<PaginatedClusterTaskSummary> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/collection-plans/list-cluster-tasks',
            path: {
                'cluster_id': clusterId,
            },
            query: {
                'sort_by': sortBy,
                'sort_order': sortOrder,
                'page': page,
                'page_size': pageSize,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Delete Task
     * @param clusterId
     * @param taskId
     * @returns any Successful Response
     * @throws ApiError
     */
    public deleteTask(
        clusterId: string,
        taskId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/clusters/{cluster_id}/collection-plans/delete-task/{task_id}',
            path: {
                'cluster_id': clusterId,
                'task_id': taskId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Mark Task Complete
     * Called when a Cluster marks a task as complete without submitting data
     * @param clusterId
     * @param taskId
     * @param completedReason
     * @returns any Successful Response
     * @throws ApiError
     */
    public markTaskComplete(
        clusterId: string,
        taskId: string,
        completedReason?: (CompletedReasonEnum | null),
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/clusters/{cluster_id}/collection-plans/mark-task-complete/{task_id}',
            path: {
                'cluster_id': clusterId,
                'task_id': taskId,
            },
            query: {
                'completed_reason': completedReason,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Reassign Task
     * Reassign a task to different users
     * @param clusterId
     * @param taskId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public reassignTask(
        clusterId: string,
        taskId: string,
        requestBody: ReassignTaskPayload,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/clusters/{cluster_id}/collection-plans/reassign-task/{task_id}',
            path: {
                'cluster_id': clusterId,
                'task_id': taskId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Bulk Reassign Tasks
     * Bulk reassign multiple tasks to different users
     * @param clusterId
     * @param taskId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public bulkReassignTasks(
        clusterId: string,
        taskId: string,
        requestBody: BulkReassignTasksPayload,
    ): CancelablePromise<Record<string, any>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/collection-plans/bulk-reassign-tasks',
            path: {
                'cluster_id': clusterId,
            },
            query: {
                'task_id': taskId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Bulk Update Due Dates
     * Bulk update due dates for multiple tasks
     * @param clusterId
     * @param taskId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public bulkUpdateDueDates(
        clusterId: string,
        taskId: string,
        requestBody: BulkUpdateDueDatePayload,
    ): CancelablePromise<Record<string, any>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/collection-plans/bulk-update-due-dates',
            path: {
                'cluster_id': clusterId,
            },
            query: {
                'task_id': taskId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Bulk Mark Complete
     * Bulk mark multiple tasks as complete
     * @param clusterId
     * @param taskId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public bulkMarkComplete(
        clusterId: string,
        taskId: string,
        requestBody: BulkMarkCompletePayload,
    ): CancelablePromise<Record<string, any>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/collection-plans/bulk-mark-complete',
            path: {
                'cluster_id': clusterId,
            },
            query: {
                'task_id': taskId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Bulk Delete Tasks
     * Bulk delete multiple tasks
     * @param clusterId
     * @param taskId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public bulkDeleteTasks(
        clusterId: string,
        taskId: string,
        requestBody: BulkDeleteTasksPayload,
    ): CancelablePromise<Record<string, any>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/collection-plans/bulk-delete-tasks',
            path: {
                'cluster_id': clusterId,
            },
            query: {
                'task_id': taskId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Cluster Interpolated Body By Block Id
     * @param clusterId
     * @param requestBody
     * @returns string Successful Response
     * @throws ApiError
     */
    public getClusterInterpolatedBodyByBlockId(
        clusterId: string,
        requestBody: BlockInterpolationPayload,
    ): CancelablePromise<Record<string, string>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/collection-plans/get-cluster-interpolated-body-by-block',
            path: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Compliance Statuses For Entities
     * @param clusterId
     * @param namespaceId
     * @param requestBody
     * @returns EntityComplianceStatuses Successful Response
     * @throws ApiError
     */
    public listComplianceStatusesForEntities(
        clusterId: string,
        namespaceId: string,
        requestBody: ListComplianceStatusPayload,
    ): CancelablePromise<Array<EntityComplianceStatuses>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/collection-plans/list-compliance-statuses-for-entities',
            path: {
                'cluster_id': clusterId,
            },
            query: {
                'namespace_id': namespaceId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Export Compliance Statuses For Entities
     * @param clusterId
     * @param namespaceId
     * @param requestBody
     * @returns string Successful Response
     * @throws ApiError
     */
    public exportComplianceStatusesForEntities(
        clusterId: string,
        namespaceId: string,
        requestBody: ListComplianceStatusPayload,
    ): CancelablePromise<Record<string, string>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/collection-plans/export-compliance-statuses-for-entities',
            path: {
                'cluster_id': clusterId,
            },
            query: {
                'namespace_id': namespaceId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Deliverables With Entities With Statuses
     * @param clusterId
     * @param namespaceId
     * @param requestBody
     * @returns DeliverableWithEntitiesWithStatuses Successful Response
     * @throws ApiError
     */
    public listDeliverablesWithEntitiesWithStatuses(
        clusterId: string,
        namespaceId: string,
        requestBody: ListDeliverablesWithEntitiesPayload,
    ): CancelablePromise<Array<DeliverableWithEntitiesWithStatuses>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/collection-plans/list-deliverables-with-entities-with-statuses',
            path: {
                'cluster_id': clusterId,
            },
            query: {
                'namespace_id': namespaceId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Export Compliance Statuses For Deliverables
     * @param clusterId
     * @param namespaceId
     * @param requestBody
     * @returns string Successful Response
     * @throws ApiError
     */
    public exportComplianceStatusesForDeliverables(
        clusterId: string,
        namespaceId: string,
        requestBody: ListDeliverablesWithEntitiesPayload,
    ): CancelablePromise<Record<string, string>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/collection-plans/export-compliance-statuses-for-deliverables',
            path: {
                'cluster_id': clusterId,
            },
            query: {
                'namespace_id': namespaceId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Update Deliverable Normalized Name
     * @param clusterId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public updateDeliverableNormalizedName(
        clusterId: string,
        requestBody: UpdateDeliverableNormalizedNamePayload,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/collection-plans/update-deliverable-normalized-name/',
            path: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Audit Logs For Deliverables For Cluster
     * @param clusterId
     * @param cursor
     * @param pageSize
     * @returns PaginatedResult Successful Response
     * @throws ApiError
     */
    public listAuditLogsForDeliverablesForCluster(
        clusterId: string,
        cursor?: (string | null),
        pageSize: number = 20,
    ): CancelablePromise<PaginatedResult> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/clusters/{cluster_id}/collection-plans/list-audit-logs-for-deliverables-for-cluster',
            path: {
                'cluster_id': clusterId,
            },
            query: {
                'cursor': cursor,
                'page_size': pageSize,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

}
